/**
 * The consultant on an Acquisition study (docs/plans/2026-10-11_acquisition.md, "The Acquisition tab", Ask the
 * consultant; P3c). Two things, both pure:
 *
 *   - what "Ask the consultant" sends from a study's page: the study's id and the point keys of the rows on screen (at
 *     most 200), nothing else of the page. The server reads the study itself (platform-core/internal/twin,
 *     copilot_acquisition.go) and hands the brain its digest;
 *   - what an acquisition_change card comes to against the study as it stands now: each change applies, or is skipped
 *     because its row was decided otherwise since the consultant read the study (the row's decided_at at or after the
 *     card's read_at, the time of the claim the platform stamped on the card, else its message's time for a card made
 *     before that stamp; and another decision), because the row is already so, or because the study no longer holds
 *     it. A card whose time cannot be read applies nothing: it is never taken to be older than it is. Applying sends
 *     only the changes that apply, as PUT .../decisions takes them, and says how many it applied and skipped.
 */
import type {
  AcquisitionDecisionIn,
  AcquisitionDecisionKind,
  AcquisitionEdit,
  AcquisitionReviewRow,
  AcquisitionStudy,
} from "@/lib/api/acquisition";
import type { AcquisitionChange } from "@/lib/api/copilot-imports";
import { ABOUT_POINT_KEYS_MAX, aboutLabel, isPointKey, type CopilotAbout } from "@/lib/copilot/ask";
import { panelContent, type CopilotAvailability } from "@/lib/copilot/availability";
import { canAskTheBrain } from "@/lib/imports/copilot";
import { count, rateWords, secondsWords } from "./review";

/** The most changes a card holds (the server's copilotChangesMax). */
export const CHANGES_MAX = 50;

/**
 * The keys of the rows on screen: the first `shown` of the rows the table holds for its filter and search, in that
 * order, at most 200, each one the server takes in a context.
 */
export function rowsOnScreen(rows: readonly Pick<AcquisitionReviewRow, "point_key">[], shown: number): string[] {
  return rows
    .slice(0, Math.max(0, Math.min(shown, rows.length)))
    .map((r) => r.point_key)
    .filter(isPointKey)
    .slice(0, ABOUT_POINT_KEYS_MAX);
}

/**
 * Whether a study's page offers "Ask the consultant": where the consultant answers in the panel beside the page
 * (panelContent: the platform answers the person's thread list, and this is not the consultant's own page), to a person
 * who may ask it (canAskTheBrain: an owner, an admin or a member; a viewer reads the consultant's threads, never asks).
 */
export function askConsultantShown(availability: CopilotAvailability, pathname: string, role: string | null): boolean {
  return panelContent(availability, pathname) === "conversation" && canAskTheBrain(role);
}

/** What "Ask the consultant" sends: the study's page, its id and the keys of the rows on screen, and the chip's words. */
export function studyAbout(study: Pick<AcquisitionStudy, "id"> & { source: { name: string } }, keys: readonly string[]): CopilotAbout {
  return {
    page: "acquisition_study",
    study_id: study.id.toLowerCase(),
    point_keys: keys.filter(isPointKey).slice(0, ABOUT_POINT_KEYS_MAX),
    label: aboutLabel(`Acquisition study: ${study.source.name}`, "acquisition_study"),
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const DECISIONS = new Set<string>(["accept", "edit", "reject"]);

/**
 * A card's changes as the page applies them, read defensively from what the server sent (it checked them before storing
 * the card): 1 to 50, each a point's key, accept, edit or reject, an edit's fields with an edit, and a reason. null when
 * any does not read.
 */
export function changesOf(raw: unknown): AcquisitionChange[] | null {
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > CHANGES_MAX) return null;
  const out: AcquisitionChange[] = [];
  for (const c of raw) {
    if (!isRecord(c) || typeof c.point_key !== "string" || c.point_key.trim() === "") return null;
    if (typeof c.decision !== "string" || !DECISIONS.has(c.decision)) return null;
    const change: AcquisitionChange = {
      point_key: c.point_key,
      decision: c.decision as AcquisitionDecisionKind,
      reason: typeof c.reason === "string" ? c.reason : "",
    };
    if (c.decision === "edit") {
      if (!isRecord(c.edited)) return null;
      change.edited = c.edited as AcquisitionEdit;
    }
    out.push(change);
  }
  return out;
}

/** A change as PUT .../decisions takes it: an edit with its fields, accept and reject alone. */
export function decisionOf(change: AcquisitionChange): AcquisitionDecisionIn {
  return change.decision === "edit"
    ? { point_key: change.point_key, decision: "edit", edited: change.edited ?? {} }
    : { point_key: change.point_key, decision: change.decision };
}

/** A JSON value in one form, its objects' keys in order: two edits compare by the fields they set and their values. */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (isRecord(v)) {
    return `{${Object.keys(v)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

/** Whether a row is decided as a change decides it: the same decision and, for an edit, the same fields set alike. */
export function sameAsChange(
  row: Pick<AcquisitionReviewRow, "decision" | "edited">,
  change: Pick<AcquisitionChange, "decision" | "edited">,
): boolean {
  if (row.decision !== change.decision) return false;
  return change.decision !== "edit" || canonical(row.edited ?? {}) === canonical(change.edited ?? {});
}

/** Whether a study's rows hold every decision as sent: a save whose answer was lost made them. */
export function decisionsHeld(
  decisions: readonly AcquisitionDecisionIn[],
  rows: readonly Pick<AcquisitionReviewRow, "point_key" | "decision" | "edited">[],
): boolean {
  const byKey = new Map(rows.map((r) => [r.point_key, r] as const));
  return decisions.every((d) => {
    const row = byKey.get(d.point_key);
    return row !== undefined && sameAsChange(row, { decision: d.decision, edited: "edited" in d ? d.edited : undefined });
  });
}

/** What becomes of a change when the card is applied. */
export type ChangeState = "apply" | "decided_since" | "already" | "gone" | "time_unknown";

export interface ChangeView {
  change: AcquisitionChange;
  state: ChangeState;
  row?: AcquisitionReviewRow;
}

/** A time as the page compares it: its milliseconds, or null when it is no time. */
function timeOf(v: unknown): number | null {
  if (typeof v !== "string" || v.trim() === "") return null;
  const ms = Date.parse(v);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * The time a card's rows are compared against, or null when it cannot be read. The card's spec carries read_at, the
 * time of the claim the consultant read the study at (the platform sets it when the reply is stored: the brain cannot);
 * a card made before that stamp has none, and reads as of its message's time (`madeAt`), later than the read, so a row
 * decided in between is taken for older than it is: the most such a card can do. A card with neither time, or whose
 * times do not read, has no time at all: null, and nothing is applied from it (changeApplyPlan).
 */
export function cardTime(spec: unknown, madeAt: string | null | undefined): string | null {
  const readAt = isRecord(spec) ? spec.read_at : undefined;
  if (readAt !== undefined && readAt !== null && readAt !== "") {
    // A stamp that is there but does not read is no time: the card refuses, rather than read as of its later message.
    return timeOf(readAt) !== null ? (readAt as string) : null;
  }
  return timeOf(madeAt) !== null ? (madeAt as string) : null;
}

/**
 * How long before the card's time a decision still counts as made after it. A decision's decided_at is its
 * transaction's start, so one begun just before the consultant read the study and committed after the read is missing
 * from what it read, yet older than the card's time: within this margin it counts as made after the read, and the
 * card skips its row rather than overwrite a decision the consultant never saw (#1369 review).
 */
export const SINCE_MARGIN_MS = 5000;

/**
 * Each change against the study as it stands. `since` is the card's time (cardTime: when the consultant read the study):
 *   - time_unknown: the card's time cannot be read (`since` null): every change, none judged;
 *   - gone: the study holds no row of its point;
 *   - already: the row is decided as the change decides it, whenever that was (nothing to send);
 *   - decided_since: the row was decided otherwise at or after `since`, which the consultant had not read: the person's
 *     decision stands (at the same millisecond counts as after: a row never goes on the card's word over a decision it
 *     may not have seen);
 *   - apply: otherwise (an undecided row, or one decided otherwise before `since`, which the consultant read).
 */
export function changeViews(
  changes: readonly AcquisitionChange[],
  rows: readonly AcquisitionReviewRow[],
  since: string | null,
): ChangeView[] {
  const made = timeOf(since);
  if (made === null) return changes.map((change) => ({ change, state: "time_unknown" }));
  const byKey = new Map(rows.map((r) => [r.point_key, r] as const));
  return changes.map((change) => {
    const row = byKey.get(change.point_key);
    if (!row) return { change, state: "gone" };
    if (sameAsChange(row, change)) return { change, state: "already", row };
    const decided = timeOf(row.decided_at);
    if (row.decision && decided !== null && decided >= made - SINCE_MARGIN_MS) return { change, state: "decided_since", row };
    return { change, state: "apply", row };
  });
}

/**
 * What applying a card sends (the changes that apply, as PUT .../decisions takes them), and what it skips, with why.
 * `timeUnknown`: the card's time cannot be read, so nothing is sent and every change is skipped (changeViews).
 */
export function changeApplyPlan(
  changes: readonly AcquisitionChange[],
  rows: readonly AcquisitionReviewRow[],
  since: string | null,
): { send: AcquisitionDecisionIn[]; skipped: ChangeView[]; timeUnknown: boolean } {
  const views = changeViews(changes, rows, since);
  return {
    send: views.filter((v) => v.state === "apply").map((v) => decisionOf(v.change)),
    skipped: views.filter((v) => v.state !== "apply"),
    timeUnknown: views.some((v) => v.state === "time_unknown"),
  };
}

/** Why a change is skipped, in words; nothing for one that applies. */
export const CHANGE_STATE_WORDS: Record<ChangeState, string> = {
  apply: "",
  decided_since: "Decided otherwise since the consultant read the study: skipped.",
  already: "Already decided so: skipped.",
  gone: "No longer in the study: skipped.",
  time_unknown: "The time of this card could not be read: not applied.",
};

/** When applying would send nothing. */
export const NOTHING_TO_APPLY = "Nothing is left to apply: each row was decided since, or is already so.";

/** When the card's time cannot be read: nothing is applied from it, and the person is told so. */
export const CARD_TIME_UNKNOWN = "The time of this card could not be read, so nothing was applied.";

/**
 * What applying a card did, in words, for the card to keep (the outcome's summary, one line): how many rows it applied
 * and how many it skipped, and why. "3 applied, 2 skipped because they were decided since the card", "3 applied, none
 * skipped"; when the skips differ: "3 applied, 4 skipped: 2 decided since the card, 1 already so, 1 no longer in the
 * study".
 */
export function appliedWords(applied: number, skipped: readonly Pick<ChangeView, "state">[]): string {
  const by = (s: ChangeState) => skipped.filter((v) => v.state === s).length;
  const lead = `${applied.toLocaleString("en-US")} applied`;
  if (skipped.length === 0) return `${lead}, none skipped`;
  const since = by("decided_since");
  const n = skipped.length.toLocaleString("en-US");
  if (since === skipped.length) {
    return `${lead}, ${n} skipped because ${skipped.length === 1 ? "it was" : "they were"} decided since the card`;
  }
  const why = [
    [since, "decided since the card"],
    [by("already"), "already so"],
    [by("gone"), "no longer in the study"],
  ] as const;
  const parts = why.filter(([k]) => k > 0).map(([k, w]) => `${k.toLocaleString("en-US")} ${w}`);
  return `${lead}, ${n} skipped${parts.length > 0 ? `: ${parts.join(", ")}` : ""}`;
}

export const DECISION_WORDS: Record<AcquisitionDecisionKind, string> = { accept: "Accept", edit: "Edit", reject: "Reject" };

/** Names for the ids an edit carries, where the page knows them; the id itself otherwise. */
export interface EditNames {
  point?: (key: string) => string;
  station?: (id: string) => string;
}

/**
 * What an edit sets, in words, field by field in the editor's order: "parameter pour_temperature; read every 30 s
 * running, 2 min idle; heartbeat 10 min; trigger MC_7_MouldCount". A field it clears says so.
 */
export function editWords(edited: AcquisitionEdit | undefined, names: EditNames = {}): string {
  if (!edited) return "";
  const parts: string[] = [];
  const set = (key: keyof AcquisitionEdit) => Object.prototype.hasOwnProperty.call(edited, key);
  const cleared = (what: string) => `${what} cleared`;
  if (set("station_id")) parts.push(edited.station_id ? `station ${names.station?.(edited.station_id) ?? edited.station_id}` : cleared("station"));
  if (set("entity_id")) parts.push(edited.entity_id ? `equipment ${edited.entity_id}` : cleared("equipment"));
  if (set("param")) parts.push(edited.param ? `parameter ${edited.param}` : cleared("parameter"));
  if (set("unit")) parts.push(edited.unit ? `unit ${edited.unit}` : cleared("unit"));
  if (set("scale")) parts.push(typeof edited.scale === "number" ? `scale ${edited.scale}` : cleared("scale"));
  if (set("keep")) parts.push(edited.keep ? "kept" : "dropped");
  if (set("rate") && edited.rate) parts.push(`read ${rateWords(edited.rate).toLowerCase()}`);
  if (set("deadband")) parts.push(typeof edited.deadband === "number" ? `deadband ${edited.deadband}` : cleared("deadband"));
  if (set("heartbeat")) {
    const h = edited.heartbeat;
    parts.push(typeof h === "number" ? (h === 0 ? "no heartbeat" : `heartbeat ${secondsWords(h)}`) : cleared("heartbeat"));
  }
  if (set("trigger")) parts.push(edited.trigger ? `trigger ${names.point?.(edited.trigger) ?? edited.trigger}` : cleared("trigger"));
  return parts.join("; ");
}

/** One change as its card lists it: the point, the decision, what it sets, why, and what becomes of it (null: unknown yet). */
export interface ChangeLine {
  key: string;
  point: string;
  decision: string;
  what: string;
  reason: string;
  state: ChangeState | null;
}

/**
 * A card's changes as it lists them: by the point's name where the study is read (else its key), each with what it
 * becomes when applied (views: changeViews, null while the study is read).
 */
export function changeLines(
  changes: readonly AcquisitionChange[],
  views: readonly ChangeView[] | null,
  names: EditNames = {},
): ChangeLine[] {
  return changes.map((change, i) => {
    const view = views?.[i];
    return {
      key: `${i}-${change.point_key}`,
      point: view?.row?.name ?? names.point?.(change.point_key) ?? change.point_key,
      decision: DECISION_WORDS[change.decision],
      what: change.decision === "edit" ? editWords(change.edited, names) : "",
      reason: change.reason,
      state: view ? view.state : null,
    };
  });
}

/** A card's changes counted by decision, for its line under the title: "3 rows: 1 accept, 1 edit, 1 reject". */
export function changesSummary(changes: readonly Pick<AcquisitionChange, "decision">[]): string {
  const by = (d: AcquisitionDecisionKind) => changes.filter((c) => c.decision === d).length;
  const parts = (["accept", "edit", "reject"] as const)
    .map((d) => [d, by(d)] as const)
    .filter(([, n]) => n > 0)
    .map(([d, n]) => `${n} ${d}`);
  return `${count(changes.length, "row")}: ${parts.join(", ")}`;
}
